import { State, Action, StateContext, Selector, Store, Select } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { Injectable } from '@angular/core';
import * as ContractActions from './contract.action';
import { ContractService } from './contract.service';
import * as ContractModels from '../../models/contract/contract';
import { BannerService } from '../../shared/services/banner.service';
import { HelperService } from '../helper/helper.service';
import * as Models from "../../models/commonModels";
import { saveAs } from "file-saver";

export interface ContractStateModel {
  List: Models.ResponseList<ContractModels.ContractPreviewModel>;
  Contract: Models.Response<ContractModels.ContractModel>;
  PreviousContract: Models.Response<ContractModels.ContractModel>;
  Search: ContractModels.ContractSearchModel;
  DownloadSuccess: boolean;
  DiscountList: Models.ResponseList<ContractModels.ContractDiscountPreviewModel>;
  GarageList: Models.ResponseList<ContractModels.ContractGaragePreviewModel>;
  PriceList: Models.ResponseList<ContractModels.ContractGarageServicePriceModel>;
  ExtraParametersList: Models.ResponseList<ContractModels.ContractExtraParameterModel>;
  ExtraParameter: Models.Response<ContractModels.ContractExtraParameterModel>;
  ImportContractDiscountStatus: boolean;
  ImportContractPriceListStatus: boolean;
  Report: Models.ReportProcessing;
  SubCustomerPriceList: Models.ResponseList<ContractModels.ContractSubCustomerPricePreviewModel>;
  SubCustomerExtraParameterList: Models.ResponseList<ContractModels.ContractSubCustomerExtraParametersPreviewModel>;
  ActiveContract: Models.Response<ContractModels.ContractModel>;
}

@State<ContractStateModel>({
  name: 'ContractState',
  defaults: {
    List: null,
    Contract: null,
    PreviousContract: null,
    Search: null,
    DownloadSuccess: null,
    DiscountList: null,
    GarageList: null,
    PriceList: null,
    ExtraParametersList: null,
    ExtraParameter: null,
    ImportContractDiscountStatus: null,
    Report: null,
    ImportContractPriceListStatus: null,
    SubCustomerPriceList: null,
    SubCustomerExtraParameterList: null,
    ActiveContract: null,
  }
})

@Injectable()
export class ContractState {
  constructor(
    private helperService: HelperService,
    private contractService: ContractService,
    private bannerService: BannerService
  ) { }

  @Selector()
  static List(state: ContractStateModel) {
    return state.List;
  }

  @Selector()
  static Search(state: ContractStateModel) {
    return state.Search;
  }

  @Selector()
  static Contract(state: ContractStateModel) {
    return state.Contract;
  }

  @Selector()
  static DownloadSuccess(state: ContractStateModel) {
    return state.DownloadSuccess;
  }

  @Selector()
  static DiscountList(state: ContractStateModel) {
    return state.DiscountList;
  }

  @Selector()
  static GarageList(state: ContractStateModel) {
    return state.GarageList;
  }

  @Selector()
  static PriceList(state: ContractStateModel) {
    return state.PriceList;
  }

  @Selector()
  static ExtraParametersList(state: ContractStateModel) {
    return state.ExtraParametersList;
  }

  @Selector()
  static ExtraParameter(state: ContractStateModel) {
    return state.ExtraParameter;
  }

  @Selector()
  static PreviousContract(state: ContractStateModel) {
    return state.PreviousContract;
  }

  @Selector()
  static ImportContractDiscountStatus(state: ContractStateModel) {
    return state.ImportContractDiscountStatus;
  }

  @Selector()
  static ImportContractPriceListStatus(state: ContractStateModel) {
    return state.ImportContractPriceListStatus;
  }

  @Selector()
  static SubCustomerExtraParameterList(state: ContractStateModel) {
    return state.SubCustomerExtraParameterList;
  }

  @Selector()
  static SubCustomerPriceList(state: ContractStateModel) {
    return state.SubCustomerPriceList;
  }

  @Selector()
  static ActiveContract(state: ContractStateModel) {
    return state.ActiveContract;
  }

  @Action(ContractActions.GetList)
  getList(
    context: StateContext<ContractStateModel>,
    action: ContractActions.GetList
  ) {
    context.setState(
      patch({
        List: null
      })
    );

    this.contractService.getList(action.Search).subscribe(
      (data) => {
        if (data.Success) {

          context.setState(
            patch({
              List: data,
            })
          );
        }
      },
      (error) => {
        this.bannerService.error("Błąd pobierania listy kontraktów.");
        this.helperService.SaveException("ContractActions.GetList", error);
      }
    );
  }

  @Action(ContractActions.SetContractSearch)
  setGarageSearch(
    context: StateContext<ContractStateModel>,
    action: ContractActions.SetContractSearch
  ) {
    action.Search.CurrentPage = 1;

    context.setState(
      patch({
        Search: action.Search,
      })
    );

    this.helperService.consoleLog(action.Search);

    context.dispatch(new ContractActions.GetList(action.Search));
  }

  @Action(ContractActions.SetContractListPage)
  setGarageListPage(
    context: StateContext<ContractStateModel>,
    action: ContractActions.SetContractListPage
  ) {
    context.setState(
      patch({
        Search: action.Search,
      })
    );

    this.helperService.consoleLog(action.Search);
    context.dispatch(new ContractActions.GetList(action.Search));
  }

  @Action(ContractActions.ResetContractSearch)
  resetGarageSearch(context: StateContext<ContractStateModel>) {
    const state = context.getState();

    const newSearch = new ContractModels.ContractSearchModel();

    context.setState(
      patch({
        Search: newSearch,
      })
    );

    context.dispatch(new ContractActions.GetList(newSearch));
  }

  @Action(ContractActions.Set)
  set(
    context: StateContext<ContractStateModel>,
    action: ContractActions.Set
  ) {
    const errorMessage = "Błąd aktualizacji Kontraktu.";

    this.contractService.set(action.Set).subscribe(
      (ret) => {
        this.bannerService.success(
          `Kontrakt został zaaktualizowany.`
        );
        context.setState(
          patch({
            Contract: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException(
          "ContractActions.Set",
          error
        );

        context.setState(
          patch({
            Contract: null
          })
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.SetStatus)
  setStatus(
    context: StateContext<ContractStateModel>,
    action: ContractActions.SetStatus
  ) {
    const errorMessage = "Błąd zmiany Statusu.";

    this.contractService.setStatus(action.Set).subscribe(
      (ret) => {
        this.bannerService.success(
          `Status został zmieniony.`
        );
        context.setState(
          patch({
            Contract: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException(
          "ContractActions.SetStatus",
          error
        );

        context.setState(
          patch({
            Contract: null
          })
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.Active)
  active(
    context: StateContext<ContractStateModel>,
    action: ContractActions.Active
  ) {
    const errorMessage = "Błąd aktualizacji Kontraktu.";

    this.contractService.active(action.Set).subscribe(
      (ret) => {
        this.bannerService.success(
          `Kontrakt został zaaktualizowany.`
        );
        context.setState(
          patch({
            Contract: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException(
          "ContractActions.Active",
          error
        );

        context.setState(
          patch({
            Contract: null
          })
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.Get)
  get(
    context: StateContext<ContractStateModel>,
    action: ContractActions.Get
  ) {
    const errorMessage = "Błąd pobierania danych o kontrakcie.";

    this.contractService.get(action.Contract).subscribe(
      (ret) => {
        context.setState(
          patch({
            Contract: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException("ContractActions.Get", error);

        context.setState(
          patch({
            Contract: null
          })
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.GetActiveForCustomer)
  getActiveForCustomer(
    context: StateContext<ContractStateModel>,
    action: ContractActions.GetActiveForCustomer
  ) {
    const errorMessage = "Błąd pobierania aktywnego kontraktu.";

    this.contractService.getActiveForCustomer(action.Contract).subscribe(
      (ret) => {
        context.setState(
          patch({
            ActiveContract: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException("ContractActions.GetActiveForCustomer", error);

        context.setState(
          patch({
            ActiveContract: null
          })
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.Create)
  create(
    context: StateContext<ContractStateModel>,
    action: ContractActions.Create
  ) {
    const errorMessage = "Błąd aktualizacji Kontraktu.";

    this.contractService.create(action.Create).subscribe(
      (ret) => {
        this.bannerService.success(
          `Kontrakt został utworzony.`
        );
        context.setState(
          patch({
            Contract: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException(
          "ContractActions.Create",
          error
        );

        context.setState(
          patch({
            Contract: null
          })
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.PublishSend)
  publishSend(
    context: StateContext<ContractStateModel>,
    action: ContractActions.PublishSend
  ) {
    const errorMessage = "Błąd publikacji oferty.";

    this.contractService.publishSend(action.Contract).subscribe(
      (ret) => {
        if (ret.Success == true) {
          this.bannerService.success(
            `Oferta została opublikowana i wysłana.`
          );
        }

        context.setState(
          patch({
            Contract: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException(
          "ContractActions.PublishSend",
          error
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.SendAgain)
  sendAgain(
    context: StateContext<ContractStateModel>,
    action: ContractActions.SendAgain
  ) {
    const errorMessage = "Błąd wysyłania oferty.";

    this.contractService.sendAgain(action.Contract).subscribe(
      (ret) => {
        if (ret.Success == true) {
          this.bannerService.success(
            `Oferta została wysłana ponownie.`
          );
        }

        context.setState(
          patch({
            Contract: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException(
          "ContractActions.SendAgain",
          error
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.ApproveForClient)
  approveForClient(
    context: StateContext<ContractStateModel>,
    action: ContractActions.ApproveForClient
  ) {
    const errorMessage = "Błąd zatwierdzanai oferty w imieniu klienta.";

    this.contractService.approveForClient(action.Contract).subscribe(
      (ret) => {
        this.bannerService.success(
          `Oferta została zatiwerdzona.`
        );
        context.setState(
          patch({
            Contract: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException(
          "ContractActions.ApproveForClient",
          error
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.Download)
  getGarageServicePricePdfReport(
    context: StateContext<ContractStateModel>,
    action: ContractActions.Download
  ) {
    function patchContext(loading: boolean) {
      context.setState(
        patch({
          DownloadSuccess: loading,
        })
      );
    }

    patchContext(true);
    const fileName = `Oferta-${action.Contract.Item.CustomerName}.pdf`;
    this.bannerService.info(
      `Rozpoczęto generowanie oferty kontraktu ${fileName}, proszę czekać ...`,
      "Export danych"
    );

    this.contractService.download(action.Contract).subscribe(
      (res) => {
        const file = new File(
          [res],
          `${fileName}-${this.helperService.TimeStamp()}.pdf`,
          {
            type:
              "application/pdf",
          }
        );
        saveAs(file);
        patchContext(false);

        action.Contract.End();
      },
      (res) => {
        patchContext(false);
        this.bannerService.error(`Błąd pobierania raportu: ${fileName}`);
      },
      () => {
        patchContext(false);
        this.helperService.consoleLog(
          "@Action(ContractActions.Download)"
        );
      }
    );
  }

  @Action(ContractActions.GetDiscount)
  getDiscount(
    context: StateContext<ContractStateModel>,
    action: ContractActions.GetDiscount
  ) {
    const errorMessage = "Błąd pobierania rabatów .";

    this.contractService.getDiscount(action.Contract).subscribe(
      (ret) => {
        context.setState(
          patch({
            DiscountList: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException("ContractActions.GetDiscount", error);

        context.setState(
          patch({
            DiscountList: null
          })
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.SetContractDiscount)
  setContractDiscount(
    context: StateContext<ContractStateModel>,
    action: ContractActions.SetContractDiscount
  ) {
    const errorMessage = "Błąd aktualizacji Rabatów.";

    this.contractService.setDiscount(action.Discount).subscribe(
      (ret) => {
        this.bannerService.success(
          `Rabaty dla kontraktu zostały zaktualizowane.`
        );
        context.setState(
          patch({
            DiscountList: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException(
          "ContractActions.SetContractDiscount",
          error
        );

        context.setState(
          patch({
            DiscountList: null
          })
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.GetGarages)
  getGarages(
    context: StateContext<ContractStateModel>,
    action: ContractActions.GetGarages
  ) {
    const errorMessage = "Błąd pobierania listy serwisów.";

    this.contractService.getGarages(action.Search).subscribe(
      (ret) => {
        context.setState(
          patch({
            GarageList: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException("ContractActions.GetGarages", error);

        context.setState(
          patch({
            GarageList: null
          })
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.GetContractGarageServicePrice)
  getContractGarageServicePrice(
    context: StateContext<ContractStateModel>,
    action: ContractActions.GetContractGarageServicePrice
  ) {
    const errorMessage = "Błąd pobierania listy serwisów.";

    this.contractService.getContractGarageServicePriceList(action.Search).subscribe(
      (ret) => {
        context.setState(
          patch({
            PriceList: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException("ContractActions.GetContractGarageServicePrice", error);

        context.setState(
          patch({
            PriceList: null
          })
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.SetContractGarageServicePrice)
  setContractGarageServicePrice(
    context: StateContext<ContractStateModel>,
    action: ContractActions.SetContractGarageServicePrice
  ) {
    const errorMessage = "Błąd aktualizacji Cennika usług.";

    this.contractService.setContractGarageServicePriceList(action.Set).subscribe(
      (ret) => {
        if (ret.Success == true) {
          this.bannerService.success(
            `Cennik usług dla kontraktu został zaktualizowany.`
          );
          context.setState(
            patch({
              PriceList: ret
            })
          );
        }
      },
      (error) => {
        this.helperService.SaveException(
          "ContractActions.SetContractGarageServicePrice",
          error
        );

        context.setState(
          patch({
            PriceList: null
          })
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.ClearContractGarageServicePrice)
  clearContractGarageServicePrice(
    context: StateContext<ContractStateModel>,
    action: ContractActions.ClearContractGarageServicePrice
  ) {
    const errorMessage = "Błąd aktualizacji Cennika usług dla serwisu.";

    this.contractService.clearContractGarageServicePriceList(action.Clear).subscribe(
      (ret) => {
        if (ret.Success == true) {
          context.setState(
            patch({
              PriceList: ret
            })
          );
        }
      },
      (error) => {
        this.helperService.SaveException(
          "ContractActions.clearContractGarageServicePrice",
          error
        );

        context.setState(
          patch({
            PriceList: null
          })
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.GetExtraParametersList)
  getExtraParametersList(
    context: StateContext<ContractStateModel>,
    action: ContractActions.GetExtraParametersList
  ) {
    const errorMessage = "Błąd pobierania parametrów.";

    this.contractService.getExtraParametersList(action.Contract).subscribe(
      (ret) => {
        context.setState(
          patch({
            ExtraParametersList: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException("ContractActions.GetExtraParametersList", error);

        context.setState(
          patch({
            ExtraParametersList: null
          })
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.SetExtraParameters)
  setExtraParametersList(
    context: StateContext<ContractStateModel>,
    action: ContractActions.SetExtraParameters
  ) {
    const errorMessage = "Błąd aktualizacji Parametru.";

    this.contractService.setExtraParameter(action.Parameter).subscribe(
      (ret) => {
        this.bannerService.success(`Parameter został zaktualizowany.`);

        context.setState(
          patch({
            ExtraParameter: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException("ContractActions.SetExtraParameters", error);

        context.setState(
          patch({
            ExtraParameter: null
          })
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.RemoveExtraParameters)
  removeExtraParameters(
    context: StateContext<ContractStateModel>,
    action: ContractActions.RemoveExtraParameters
  ) {
    const errorMessage = "Błąd podczas usuwania Parametru.";

    this.contractService.removeExtraParameters(action.Parameter).subscribe(
      (ret) => {
        if (ret.Success == true)
          this.bannerService.success(`Parameter został usunięty.`);

        context.setState(
          patch({
            ExtraParameter: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException("ContractActions.RemoveExtraParameters", error);

        context.setState(
          patch({
            ExtraParameter: null
          })
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.GetPreviousContract)
  getPreviousContract(
    context: StateContext<ContractStateModel>,
    action: ContractActions.GetPreviousContract
  ) {
    const errorMessage = "Błąd pobierania ostatniego kontraktu.";

    this.contractService.getPreviousContract(action.Contract).subscribe(
      (ret) => {
        context.setState(
          patch({
            PreviousContract: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException("ContractActions.GetPreviousContract", error);

        context.setState(
          patch({
            PreviousContract: null
          })
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  PatchReportContext(Name: string, IsProcessing: boolean, Context: StateContext<ContractStateModel>) {
    if (IsProcessing) {
      Context.setState(
        patch({
          Report: Models.ReportProcessing.Start(Name),
        })
      );
    }
    else {
      Context.setState(
        patch({
          Report: Models.ReportProcessing.End(Name),
        })
      );
    }
  }

  @Action(ContractActions.GetContractDiscountExcelReport)
  getGarageServicePriceReport(
    context: StateContext<ContractStateModel>,
    action: ContractActions.GetContractDiscountExcelReport
  ) {

    this.PatchReportContext(action.ReportName, true, context);

    const fileName = "Rabaty Kontraktu";
    this.bannerService.info(
      `Rozpoczęto generowanie pliku raportu ${fileName}, proszę czekać ...`,
      "Export danych"
    );

    this.contractService.getContractDiscountExcelReport(action.Search).subscribe(
      (res) => {
        const file = new File(
          [res],
          `${fileName}-${this.helperService.TimeStamp()}.xlsx`,
          {
            type:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }
        );
        saveAs(file);

        this.PatchReportContext(action.ReportName, true, context);
      },
      (res) => {
        this.PatchReportContext(action.ReportName, true, context);

        this.bannerService.error(`Błąd pobierania raportu: ${fileName}`);
      },
      () => {
        this.PatchReportContext(action.ReportName, true, context);

        this.helperService.consoleLog(
          "@Action(ContractActions.GetContractDiscountExcelReport)"
        );
      }
    );
  }

  @Action(ContractActions.SetImportContractDiscountStatus)
  setImportDiscountStatus(
    context: StateContext<ContractStateModel>,
    action: ContractActions.SetImportContractDiscountStatus
  ) {
    context.setState(
      patch({
        ImportContractDiscountStatus: action.Status
      })
    );
  }

  @Action(ContractActions.ImportContractDiscountList)
  importContractDiscountList(
    context: StateContext<ContractStateModel>,
    action: ContractActions.ImportContractDiscountList
  ) {

    this.bannerService.quickInfo('Rozpoczęto import Rabatów, proszę czekać.');

    this.contractService.importContractDiscount(action.Data).subscribe(ret => {

      this.helperService.consoleLog(ret.headers);

      const processed = ret.headers.get('x-import-processed');
      const error = ret.headers.get('x-import-error');
      const result = ret.headers.get('x-import-result');
      const message = ret.headers.get('x-import-message');
      const imported = Number(ret.headers.get('x-import-imported'));

      if (result === 'OK') {

        context.dispatch(new ContractActions.SetImportContractDiscountStatus(true));

        const file = new File([ret.body], `Wynik-Importu-${this.helperService.TimeStamp()}-${action.FileName}`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        if (imported > 0) {
          this.bannerService.instantSuccess(`Zakończono import pliku <b>${action.FileName}</b><br/><b>Zaimportowano ${imported}</b> pozycji.<br/>Przetworzono <b>${processed}</b> wierszy.<br/>Znaleziono <b>${error}</b> wierszy z błędami.`);
        }
        else {
          this.bannerService.instantMessage(`Zakończono import pliku <b>${action.FileName}</b><br/><b>Nie zaimporotwano żadnych pozycji</b>.<br/>Przetworzono <b>${processed}</b> wierszy.<br/>Znaleziono <b>${error}</b> wierszy z błędami.`);
        }

        saveAs(file);
      } else {

        if (message === 'IMPORT_EXCEPTION_EMPTY_FILE') {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.FileName}</b><br/>Plik przeznaczony do importu jest pusty.`);
        } else if (message === 'IMPORT_EXCEPTION_INVALID_FILE_EXTENSION') {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.FileName}</b><br/>Plik przeznaczony do importu ma niewłaściwe rozszerzenie.`);
        } else if (message === 'IMPORT_EXCEPTION_INVALID_HEDER') {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.FileName}</b><br/>Plik przeznaczony do importu ma niewłaściwy nagłówek.`);
        } else {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.FileName}</b><br/>Wystąpił nieznany błąd podczas przetwarzania pliku.`);
        }
      }
    },
      (error) => {

        this.helperService.SaveException('GarageActions.ImportGarageDiscountList', error);

        this.bannerService.error('Błąd importu cennika.');

        context.dispatch(new ContractActions.SetImportContractDiscountStatus(false));
      },
      () => {
        context.dispatch(new ContractActions.SetImportContractDiscountStatus(false));
      }
    );
  }

  @Action(ContractActions.GetContractPriceListExcelReport)
  getContractPriceListExcelReport(
    context: StateContext<ContractStateModel>,
    action: ContractActions.GetContractPriceListExcelReport
  ) {

    this.PatchReportContext(action.ReportName, true, context);

    const fileName = "Cennik Kontraktu";
    this.bannerService.info(
      `Rozpoczęto generowanie pliku raportu ${fileName}, proszę czekać ...`,
      "Export danych"
    );

    this.contractService.getContractPriceListExcelReport(action.Search).subscribe(
      (res) => {
        const file = new File(
          [res],
          `${fileName}-${this.helperService.TimeStamp()}.xlsx`,
          {
            type:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }
        );
        saveAs(file);

        this.PatchReportContext(action.ReportName, true, context);
      },
      (res) => {
        this.PatchReportContext(action.ReportName, true, context);

        this.bannerService.error(`Błąd pobierania raportu: ${fileName}`);
      },
      () => {
        this.PatchReportContext(action.ReportName, true, context);
      }
    );
  }

  @Action(ContractActions.SetImportContractPriceListStatus)
  setImportContractPriceListStatus(
    context: StateContext<ContractStateModel>,
    action: ContractActions.SetImportContractDiscountStatus
  ) {
    context.setState(
      patch({
        ImportContractPriceListStatus: action.Status
      })
    );
  }

  @Action(ContractActions.ImportContracPriceList)
  importContracPriceList(
    context: StateContext<ContractStateModel>,
    action: ContractActions.ImportContractDiscountList
  ) {

    this.bannerService.quickInfo('Rozpoczęto import Rabatów, proszę czekać.');

    this.contractService.importContracPriceList(action.Data).subscribe(ret => {

      this.helperService.consoleLog(ret.headers);

      const processed = ret.headers.get('x-import-processed');
      const error = ret.headers.get('x-import-error');
      const result = ret.headers.get('x-import-result');
      const message = ret.headers.get('x-import-message');
      const imported = Number(ret.headers.get('x-import-imported'));

      if (result === 'OK') {

        context.dispatch(new ContractActions.SetImportContractPriceListStatus(true));

        const file = new File([ret.body], `Wynik-Importu-${this.helperService.TimeStamp()}-${action.FileName}`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        if (imported > 0) {
          this.bannerService.instantSuccess(`Zakończono import pliku <b>${action.FileName}</b><br/><b>Zaimportowano ${imported}</b> pozycji.<br/>Przetworzono <b>${processed}</b> wierszy.<br/>Znaleziono <b>${error}</b> wierszy z błędami.`);
        }
        else {
          this.bannerService.instantMessage(`Zakończono import pliku <b>${action.FileName}</b><br/><b>Nie zaimporotwano żadnych pozycji</b>.<br/>Przetworzono <b>${processed}</b> wierszy.<br/>Znaleziono <b>${error}</b> wierszy z błędami.`);
        }

        saveAs(file);
      } else {

        if (message === 'IMPORT_EXCEPTION_EMPTY_FILE') {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.FileName}</b><br/>Plik przeznaczony do importu jest pusty.`);
        } else if (message === 'IMPORT_EXCEPTION_INVALID_FILE_EXTENSION') {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.FileName}</b><br/>Plik przeznaczony do importu ma niewłaściwe rozszerzenie.`);
        } else if (message === 'IMPORT_EXCEPTION_INVALID_HEDER') {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.FileName}</b><br/>Plik przeznaczony do importu ma niewłaściwy nagłówek.`);
        } else {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.FileName}</b><br/>Wystąpił nieznany błąd podczas przetwarzania pliku.`);
        }
      }
    },
      (error) => {

        this.helperService.SaveException('GarageActions.ImportContractDiscountList', error);

        this.bannerService.error('Błąd importu cennika.');

        context.dispatch(new ContractActions.SetImportContractPriceListStatus(false));
      },
      () => {
        context.dispatch(new ContractActions.SetImportContractPriceListStatus(false));
      }
    );
  }


  @Action(ContractActions.GetSubCustomerPriceItems)
  getSubCustomerPriceItems(
    context: StateContext<ContractStateModel>,
    action: ContractActions.GetSubCustomerPriceItems
  ) {
    context.setState(
      patch({
        SubCustomerPriceList: null
      })
    );

    this.contractService.getSubCustomerPriceItems(action.Search).subscribe(
      (data) => {
        if (data.Success) {

          context.setState(
            patch({
              SubCustomerPriceList: data,
            })
          );
        }
      },
      (error) => {
        this.bannerService.error("Błąd pobierania cennika dla podfirmy.");
        this.helperService.SaveException("ContractActions.GetSubCustomerPriceItems", error);
      }
    );
  }

  @Action(ContractActions.SetSubCustomerPriceItems)
  setSubCustomerPriceItems(
    context: StateContext<ContractStateModel>,
    action: ContractActions.SetSubCustomerPriceItems
  ) {
    const errorMessage = "Błąd aktualizacji Kontraktu.";

    this.contractService.setSubCustomerPriceItems(action.Set).subscribe(
      (ret) => {
        if (ret.Success == true) {
          this.bannerService.success(
            `Kontrakt został zaaktualizowany.`
          );
        } else {
          this.bannerService.success(errorMessage);
        }
        context.setState(
          patch({
            SubCustomerPriceList: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException(
          "ContractActions.SetSubCustomerPriceItems",
          error
        );

        context.setState(
          patch({
            SubCustomerPriceList: null
          })
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.RemoveSubCustomerPriceItems)
  removeSubCustomerPriceItems(
    context: StateContext<ContractStateModel>,
    action: ContractActions.RemoveSubCustomerPriceItems
  ) {
    const errorMessage = "Błąd aktualizacji Kontraktu.";

    this.contractService.removeSubCustomerPriceItems(action.Remove).subscribe(
      (ret) => {
        if (ret.Success == true) {
          this.bannerService.success(
            `Kontrakt został usunięty.`
          );
        } else {
          this.bannerService.success(errorMessage);
        }
        context.setState(
          patch({
            SubCustomerPriceList: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException(
          "ContractActions.RemoveSubCustomerPriceItems",
          error
        );

        context.setState(
          patch({
            SubCustomerPriceList: null
          })
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.GetContractSubCustomerExtraParametersItems)
  getContractSubCustomerExtraParametersItems(
    context: StateContext<ContractStateModel>,
    action: ContractActions.GetContractSubCustomerExtraParametersItems
  ) {
    context.setState(
      patch({
        SubCustomerExtraParameterList: null
      })
    );

    this.contractService.getSubCustomerExtraParametersItems(action.Search).subscribe(
      (data) => {
        if (data.Success) {

          context.setState(
            patch({
              SubCustomerExtraParameterList: data,
            })
          );
        }
      },
      (error) => {
        this.bannerService.error("Błąd pobierania cennika dla podfirmy.");
        this.helperService.SaveException("ContractActions.GetContractSubCustomerExtraParametersItems", error);
      }
    );
  }

  @Action(ContractActions.SetContractSubCustomerExtraParametersItems)
  setContractSubCustomerExtraParametersItems(
    context: StateContext<ContractStateModel>,
    action: ContractActions.SetContractSubCustomerExtraParametersItems
  ) {
    const errorMessage = "Błąd aktualizacji Kontraktu.";

    this.contractService.setSubCustomerExtraParametersItems(action.Set).subscribe(
      (ret) => {
        if (ret.Success == true) {
          this.bannerService.success(
            `Kontrakt został zaaktualizowany.`
          );
        } else {
          this.bannerService.success(errorMessage);
        }
        context.setState(
          patch({
            SubCustomerExtraParameterList: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException(
          "ContractActions.SetContractSubCustomerExtraParametersItems",
          error
        );

        context.setState(
          patch({
            SubCustomerExtraParameterList: null
          })
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.RemoveContractSubCustomerExtraParametersItems)
  temoveSubCustomerExtraParametersItems(
    context: StateContext<ContractStateModel>,
    action: ContractActions.RemoveContractSubCustomerExtraParametersItems
  ) {
    const errorMessage = "Błąd aktualizacji Kontraktu.";

    this.contractService.removeSubCustomerExtraParametersItems(action.Remove).subscribe(
      (ret) => {
        if (ret.Success == true) {
          this.bannerService.success(
            `Kontrakt został usunięty.`
          );
        } else {
          this.bannerService.success(errorMessage);
        }
        context.setState(
          patch({
            SubCustomerExtraParameterList: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException(
          "ContractActions.RemoveContractSubCustomerExtraParametersItems",
          error
        );

        context.setState(
          patch({
            SubCustomerExtraParameterList: null
          })
        );
        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(ContractActions.SetDocumentFile)
  SetDocumentFile(
    context: StateContext<ContractStateModel>,
    action: ContractActions.SetDocumentFile
  ) {

    let errorMessage = 'Błąd przesyłania dokumentu.';

    this.contractService.setDocumentFile(action.Contract, action.File).subscribe(
      ret => {

        if (ret.Success) {
          this.bannerService.success('Dokument zapisany.');
        }
        else {
          this.bannerService.error(ret.Message);
        }

        context.setState(
          patch({
            Contract: ret
          })
        );
      },

      (error) => {

        this.helperService.SaveException('ContractActions.SetDocumentFile', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(ContractActions.GetDocumentFile)
  GetManual(
    context: StateContext<ContractStateModel>,
    action: ContractActions.GetDocumentFile
  ) {

    this.bannerService.info('Rozpoczęto pobieranie dokumentu');

    this.contractService.getDocumentFile(action.Contract).subscribe(
      res => {

        const file = new File([res.body], this.helperService.GetFileNameFromHttpResponse(res), { type: res.headers.get('Content-Type') });

        saveAs(file);
      },
      res => {
        this.bannerService.error(`Błąd pobierania pliku.`);
      },
      () => {
        this.helperService.consoleLog('ContractActions.GetDocumentFile');
      }
    );
  }

  @Action(ContractActions.GetCarCurrent)
  GetCarCurrent(
    context: StateContext<ContractStateModel>,
    action: ContractActions.GetCarCurrent
  ) {
    const errorMessage = "Błąd pobierania bieżącego kontrakdu dla danego samochodu.";

    this.contractService.getCarCurrent(action.Car).subscribe(
      (ret) => {
        context.setState(
          patch({
            Contract: ret
          })
        );
      },
      (error) => {
        this.helperService.SaveException("ContractActions.GetCarCurrent", error);

        context.setState(
          patch({
            Contract: null
          })
        );
        this.bannerService.error(errorMessage);
      }
    );
  }
}

